/**
 * The mission's clouds, drawn from **the original's own geometry**.
 *
 * ## This is not a port extension and it is not behind ENHANCED SKY
 *
 * `cloud-field.ts` records the recovery. `addCloud` registers `MISC.TBL` object
 * `10 + sizeClass`, and those three objects are already in this repo's bake as
 * `models/MISC-1{0,1,2}.json`: 16 vertices, 18 triangles, an **irregular
 * octagonal slab** — an 8-gon lid, an 8-gon floor, and three of the eight side
 * walls closed. Every one of the 18 faces is `BARON.PAL` **79 `#929292`**, flat
 * mid-grey, and every face is marked **not shadeable**. The three differ only in
 * scale exponent, 3 / 2 / 1, which is where the 4:2:1 size ladder comes from.
 *
 * So a 1990 cloud is a flat grey slab, and this module draws exactly that. The
 * one switch on it is REALISTIC WEATHER (`ds:0x726b`) — the manual: "with it
 * turned off, there will be no clouds in the sky" — which
 * `packages/engine`'s `RealismSettings.realisticWeather` already models, on by
 * default because both shipped `.PRF` files carry it.
 *
 * ## What ENHANCED SKY changes, and it is only the paint
 *
 * Same geometry, same placement, same count. The enhanced material replaces the
 * flat 79 with **three flat tones off one `BARON.PAL` ramp** — lit, mid, shadow
 * — chosen by two hard steps on `dot(normal, sun)`, and then one further step
 * down the same ramp wherever `three/cloud-noise.ts`' world-XZ field says the
 * cloud is hollow. That is `ramp-shade.ts`'s own mechanism (the sim shades a
 * face by stepping *down a ramp* from its anchor entry, never by multiplying a
 * colour) applied to an object the original happened to leave unshaded. Three
 * tones and a hard terminator is what the 1990 artists would have done with the
 * budget; a smooth Lambert is not.
 *
 * The hollow step is 2026-08-24's addition and it is the answer to a specific
 * measurement: a face is up to 11.6 km across, so `dot(normal, sun)` alone gives
 * a slab **one** tone, and an ENHANCED `cloudy` sortie sitting on a plate filled
 * the frame with a single flat `#d7d7d7`. `enhanced-sky.md` §14 has the census.
 *
 * **No shadows are cast**, under either material. There is no lighting model in
 * this port and there was none in the original.
 *
 * ## Opaque, and why that matters twice
 *
 * `transparent: true` + `depthWrite: false` rasterised **zero pixels** under
 * this renderer's reversed-Z on a pooled instanced mesh (2026-08-22,
 * `lessons.md`), so every pool in this port is opaque with an ordered-dither
 * cut instead, and so is this one. Being opaque also buys both occlusions the
 * manual asks for for free: a cloud in front of the sun hides the disc *and* its
 * halo (`clouds.c`'s `ds:0x4c36`, whose only caller in the binary is inside that
 * module), and a cloud in front of an aeroplane hides the aeroplane.
 *
 * ## Flying into one: the slab you are inside is not drawn
 *
 * A slab is `DoubleSide` (below) and **1,000 feet thick** — 305 m, which is what
 * `MISC-10`'s baked geometry measures now that `london-sea-placements.md` §9's
 * scale-exponent fix is in, and exactly what `clouds.c`'s own occlusion box for
 * the class says. A pilot spawned at 6,562 ft under a ceiling drawn from
 * 3,000-10,000 ft is therefore *inside* one a good share of the time, and from
 * in there every face of an 11.6 km plate is within `NEAR_DISSOLVE_M` of the
 * eye. The dissolve then does not stipple the slab *away*: it stipples it
 * **over the whole frame**, one pixel in sixteen, and a `cloudy` sortie renders
 * as grey pepper across the terrain, the sky and the horizon alike. That was
 * measured, and it is the single largest thing behind the 2026-08-24 report
 * that there is barely any cloud to see.
 *
 * So each instance carries its own drawn bounding box, and the slab whose box
 * holds the camera is collapsed to a zero-scale matrix for as long as it does.
 * The box is the **drawn geometry's** AABB rather than `cloud-field.ts`'s
 * recovered occlusion volume: the two now agree to 12 % horizontally and
 * exactly on thickness (`enhanced-sky.md` §14.1), but it is the polygon that
 * paints the pepper, so it is the polygon that has to be tested.
 *
 * Drawing *nothing* from inside is the honest reading and not a shortcut. The
 * original renders front faces of an opaque unlit slab, so its interior has no
 * appearance of its own, and no lane has ever captured `RB.EXE` from inside
 * cloud (`enhanced-sky.md` §8, work item 1) — so a whiteout here would be
 * invention, and a whiteout that lasts as long as it takes a scout to climb
 * 1,000 ft would be an invention that blinds the player. What being in cloud
 * *does* do in the original is a sim mechanic, not a paint one: `clouds.c`
 * drops in-cloud objects from the target list, which is `enhanced-sky.md` §8's
 * work item 4 and belongs with `sun-blind-spot.ts`.
 *
 * Outside a slab the dissolve still earns its keep — approaching one, the patch
 * of it directly ahead stipples open over the last `NEAR_DISSOLVE_M` instead of
 * being sliced by the near plane.
 */

import * as THREE from "three/webgpu";
import type { SkyCondition, TimeOfDay, Vec3 } from "@red-opus/engine";
import { placeClouds, type PlacedCloud } from "./cloud-field.ts";
import type { PaletteColour } from "./time-of-day.ts";
import { MODEL_TO_WORLD_PITCH_RAD } from "./world.ts";
import { bestDrawableLod, createLodGeometry, loadBakedModel } from "../three/baked-model.ts";
import { ditherThreshold } from "../three/ordered-dither.ts";
import { cloudFieldAtFragment } from "../three/cloud-noise.ts";

/**
 * `MISC.TBL` objects 10, 11 and 12, in `clouds.c`'s own size-class order.
 *
 * Relative to `data/baked`, because `loadBakedModel` prefixes `/baked/` itself
 * — writing the prefix here as well produced `/baked//baked/models/...`, a 404,
 * and a **silent** sky with no cloud in it, because the loader's rejection is
 * caught (a missing bake must not throw inside a render path). The first
 * capture of this layer was blank for exactly that reason.
 */
export const CLOUD_MODEL_PATHS: readonly string[] = [
  "models/MISC-10.json",
  "models/MISC-11.json",
  "models/MISC-12.json",
];

/** Lit, mid and shadow entries of one `BARON.PAL` ramp — the enhanced paint. */
export interface CloudRamp {
  readonly lit: PaletteColour;
  readonly mid: PaletteColour;
  readonly shadow: PaletteColour;
}

/**
 * Neutral 64-87 by day and at night; warm 176-188 at dawn and dusk, where cloud
 * tops take the colour of the sun lighting them. Dusk sits one step deeper into
 * the warm ramp than dawn, the same relationship `time-of-day.ts`'s twilight
 * runs have. The `mid` entries bracket the original's own flat 79 `#929292`, so
 * the enhanced paint reads as a shading of that colour rather than a new one.
 */
export const CLOUD_RAMPS: Readonly<Record<TimeOfDay, CloudRamp>> = {
  daytime: { lit: 0xd7d7d7, mid: 0xbababa, shadow: 0x929292 },
  dawn: { lit: 0xe7cfc7, mid: 0xd3c3b2, shadow: 0xa26d55 },
  dusk: { lit: 0xd3c3b2, mid: 0xcba28a, shadow: 0x926551 },
  night: { lit: 0x595959, mid: 0x343434, shadow: 0x1c1c1c },
};

/** Where the two hard steps fall on `dot(normal, sun)`. The terminator sits a little past the limb. */
const LIT_STEP = 0.45;
const MID_STEP = -0.05;
/**
 * Where `three/cloud-noise.ts`' field takes an ENHANCED slab's face one entry
 * *down* the ramp (its hollows) and one entry *up* (its thin, bright patches).
 *
 * Symmetric about the field's own mean of 0.5, which is the whole point: the
 * face a pilot sees most of the time is a slab's **underside**, and that face
 * points away from the sun, so it is already sitting on the ramp's bottom entry.
 * A field that could only darken left it exactly as flat as the authentic paint
 * — measured, 2026-08-24: an ENHANCED `cloudy` deck seen from below was
 * 148,639 px of one `#929292`, against ORIGINAL's 153,935 of the same. Letting
 * the field brighten as well as darken is what gives a cloud base its texture,
 * and it costs one more `step`.
 *
 * 0.42/0.58 leaves the middle sixth of the field on the sun's own tone, so the
 * three bands come out roughly a third each and no face is all one thing.
 */
const HOLLOW_BELOW = 0.42;
const BRIGHT_ABOVE = 0.58;
/**
 * Over the last of this range a slab dissolves out, rather than being sliced by
 * the near plane.
 *
 * **60 m, down from 900.** The number matters far more than it looks, because a
 * slab is not a small object: a class-0 plate is 11.6 km across and 305 m thick,
 * so *everything within the dissolve range of the eye* is a disc of plate of
 * that radius directly above or below the pilot — and at cruise that disc covers
 * most of the frame. At 900 m a `cloudy` sortie whose ceiling happened to land
 * near the spawn altitude rendered as **grey pepper over the entire picture**,
 * terrain and sky alike, one pixel in sixteen: measured on 2026-08-24 and the
 * chief cause of "I barely see any clouds". The dissolve is there to keep the
 * `CAMERA_NEAR_M` = 1 m near plane from slicing a hard moving edge across the
 * slab, and 60 m is sixty times the plane it is protecting and a little over a
 * second of a Camel's travel — long enough to read as a fade, short enough that
 * the softened patch is a smudge under the wing rather than the whole sky.
 *
 * It is the *outside* half of the fix. The inside half is `cullEnclosingSlabs`:
 * with a range this short a pilot who flew into a plate would be sealed in
 * opaque grey instead, and the module header records why that is not the
 * picture either.
 */
export const NEAR_DISSOLVE_M = 60;

type Vec3Node = THREE.Node<"vec3">;

function paletteVec3(colour: PaletteColour): Vec3Node {
  return THREE.TSL.vec3(((colour >> 16) & 0xff) / 255, ((colour >> 8) & 0xff) / 255, (colour & 0xff) / 255);
}

/**
 * The near-camera stipple, shared by both materials so a slab never pops
 * through the lens.
 *
 * `DoubleSide` rides along with it, and it is not a preference. `MISC-10`'s lid
 * and floor fans are wound the same way, so under `FrontSide` a cloud is
 * **invisible from below** — which is where a pilot almost always is, and which
 * is exactly what the first capture of this layer showed: a `cloudy` sortie with
 * two slabs in frame drew nothing at all. It is also the faithful reading: the
 * `.TBL` face record carries a *back* colour as well as a front one (both 79
 * here), so the original draws both sides too, and every other baked-model path
 * in this port already uses `DoubleSide` (`three/world-instancer.ts`,
 * `three/ramp-material.ts`).
 */
function nearDissolve(material: THREE.MeshBasicNodeMaterial): void {
  material.side = THREE.DoubleSide;
  const range = THREE.TSL.length(THREE.TSL.positionWorld.sub(THREE.TSL.cameraPosition));
  material.opacityNode = THREE.TSL.step(ditherThreshold(), range.div(NEAR_DISSOLVE_M).clamp(0, 1));
  material.alphaTest = 0.5;
}

/**
 * The original's paint: the model's own baked face colours, which are 18 faces
 * of flat `BARON.PAL` 79. `vertexColors` is how every other piece of baked
 * scenery in this port is drawn (`three/world-instancer.ts`), so a cloud is not
 * a special case.
 *
 * Exported, with `enhancedCloudMaterial`, so `enhanced-sky-scene.test.ts` can
 * pin the depth flags without a DOM — the arrangement `screen-sprites.ts`'s
 * `sheetMaterial` has, and for the same reason.
 */
export function authenticCloudMaterial(): THREE.MeshBasicNodeMaterial {
  const material = new THREE.MeshBasicNodeMaterial({ vertexColors: true });
  nearDissolve(material);
  return material;
}

/**
 * Two hard steps down one ramp, by the sun's own bearing — and then **one step
 * either way** off `three/cloud-noise.ts`' world-XZ field.
 *
 * The sun term alone gives one flat tone per face, and a `MISC-10` lid is
 * 11.6 km of one face: measured on 2026-08-24, an ENHANCED `cloudy` sortie sat
 * on top of a plate that filled the frame below the horizon with a **single**
 * flat `#d7d7d7`, which is not a cloud, it is a card. The user's report is a fair
 * description of it.
 *
 * So the field shifts the walk one entry down in its hollows and one entry up on
 * its bright patches. That is `three/ramp-shade.ts`'s own mechanism and nothing
 * else: a face's tone is still one of the ramp's three entries, still chosen by
 * stepping, and no colour is blended into existence. A lid comes out mottled
 * `lit`/`mid`, a slab's flank uses all three, and an underside — the face a
 * pilot spends the sortie looking at — comes out `shadow` with `mid` in its
 * thin patches.
 *
 * **`authenticCloudMaterial` is untouched by this.** The original paints all 18
 * faces flat `BARON.PAL` 79 and that is what ORIGINAL still draws, byte for
 * byte; the structure is a paint change under ENHANCED, which is the only thing
 * that treatment has ever changed about a slab.
 */
export function enhancedCloudMaterial(ramp: CloudRamp, sun: Vec3): THREE.MeshBasicNodeMaterial {
  const material = new THREE.MeshBasicNodeMaterial();
  const towardsSun = THREE.TSL.dot(THREE.TSL.normalWorld, THREE.TSL.vec3(sun.x, sun.y, sun.z));
  // 0, 1 or 2 entries up from `shadow`, by the sun — the pair of steps this
  // material has always made, summed instead of nested so the hollow can take
  // one back off.
  const bySun = THREE.TSL.step(MID_STEP, towardsSun).add(THREE.TSL.step(LIT_STEP, towardsSun));
  const field = cloudFieldAtFragment();
  // `step(field, k)` is `field <= k`, so this pair is -1 in the hollows, +1 on
  // the bright patches and 0 in the band between them.
  const texture = THREE.TSL.step(BRIGHT_ABOVE, field).sub(THREE.TSL.step(field, HOLLOW_BELOW));
  const level = bySun.add(texture).clamp(0, 2);
  const shaded = THREE.TSL.mix(paletteVec3(ramp.shadow), paletteVec3(ramp.mid), THREE.TSL.step(1, level));
  material.colorNode = THREE.TSL.mix(shaded, paletteVec3(ramp.lit), THREE.TSL.step(2, level));
  nearDissolve(material);
  return material;
}

export interface SkyCloudRequest {
  readonly sky: SkyCondition;
  readonly timeOfDay: TimeOfDay;
  /** `MissionSpec.seed`, so a sortie's cloud field is the same every time it is flown. */
  readonly seed: number;
  /** The sky object's bearing — the moon's at night. Only the enhanced paint reads it. */
  readonly sun: Vec3 | undefined;
  /** `false` draws the original's flat grey; `true` draws the ramp. */
  readonly enhanced: boolean;
}

export interface SkyClouds {
  readonly group: THREE.Group;
  /** Resolves once the baked `MISC` models have loaded and the slabs are in the group. */
  readonly onLoaded: Promise<number>;
}

/** The world-space rotation every slab carries: model Z-up into the world's Y-up (`world.ts`). */
function slabOrientation(): THREE.Quaternion {
  return new THREE.Quaternion().setFromEuler(new THREE.Euler(MODEL_TO_WORLD_PITCH_RAD, 0, 0));
}

/**
 * One slab's drawn extent in world metres, relative to its own position.
 *
 * The geometry's own AABB rotated into the world — exact rather than
 * conservative, because the box is axis-aligned in model space and the rotation
 * is a quarter turn about one axis. `undefined` when three could not compute a
 * bounding box, in which case nothing is culled and the layer behaves as it did
 * before: the cull is an improvement to a picture, never a thing correctness
 * rests on.
 */
export function slabExtentOf(geometry: THREE.BufferGeometry): THREE.Box3 | undefined {
  geometry.computeBoundingBox();
  const local = geometry.boundingBox;
  if (!local) return undefined;
  return local.clone().applyMatrix4(new THREE.Matrix4().makeRotationFromQuaternion(slabOrientation()));
}

/** Scratch, so the per-frame cull allocates nothing. */
const EYE = new THREE.Vector3();
const HIDDEN = new THREE.Matrix4().makeScale(0, 0, 0);

/**
 * Hides the slabs whose drawn volume holds the camera, and restores the rest.
 *
 * Two class-0 slabs *can* overlap — they are 11.6 km across and
 * `_BUILD_CLOUD_LIST` only asks for 8.4 km of separation — so this is a set and
 * not a single index. The instance buffer is rewritten only when the set
 * changes, which for a flight is a handful of times.
 */
function cullEnclosingSlabs(
  mesh: THREE.InstancedMesh,
  placed: readonly THREE.Box3[],
  visible: readonly THREE.Matrix4[],
  camera: THREE.Camera,
  wasInside: boolean[],
): void {
  camera.getWorldPosition(EYE);
  let changed = false;
  for (let index = 0; index < placed.length; index++) {
    const inside = placed[index]?.containsPoint(EYE) ?? false;
    if (inside === wasInside[index]) continue;
    wasInside[index] = inside;
    const matrix = visible[index];
    if (matrix) mesh.setMatrixAt(index, inside ? HIDDEN : matrix);
    changed = true;
  }
  if (changed) mesh.instanceMatrix.needsUpdate = true;
}

/** One instanced draw per size class the field actually used — at most three, usually one. */
function instanceSlabs(
  group: THREE.Group,
  clouds: readonly PlacedCloud[],
  geometries: readonly (THREE.BufferGeometry | undefined)[],
  material: THREE.Material,
): number {
  let drawn = 0;
  geometries.forEach((geometry, sizeClass) => {
    const ofClass = clouds.filter((cloud) => cloud.sizeClass === sizeClass);
    if (!geometry || ofClass.length === 0) return;
    const mesh = new THREE.InstancedMesh(geometry, material, ofClass.length);
    const extent = slabExtentOf(geometry);
    const matrices = ofClass.map((cloud) =>
      new THREE.Matrix4().compose(
        new THREE.Vector3(cloud.x, cloud.y, cloud.z),
        slabOrientation(),
        new THREE.Vector3(1, 1, 1),
      ),
    );
    matrices.forEach((matrix, index) => mesh.setMatrixAt(index, matrix));
    mesh.instanceMatrix.needsUpdate = true;
    // A class-0 slab's own geometry is 11.6 km across (`clouds.c`'s 33,750 ft
    // class, post-rescale) and its instances are scattered over 50 km, so the
    // mesh's bounding sphere has to be the one that accounts for the instance
    // matrices, not the geometry's.
    mesh.computeBoundingSphere();
    if (extent) {
      const boxes = ofClass.map((cloud) =>
        extent.clone().translate(new THREE.Vector3(cloud.x, cloud.y, cloud.z)),
      );
      const wasInside = ofClass.map(() => false);
      mesh.onBeforeRender = (_renderer, _scene, camera) =>
        cullEnclosingSlabs(mesh, boxes, matrices, camera, wasInside);
    }
    group.add(mesh);
    drawn += ofClass.length;
  });
  return drawn;
}

/**
 * The mission's weather, drawn. The group fills itself in once the three baked
 * models have loaded and stays empty if they never do — a missing bake is a sky
 * without cloud, never a throw in a render loop.
 */
export function createSkyClouds(request: SkyCloudRequest): SkyClouds {
  const group = new THREE.Group();
  const clouds = placeClouds(request.sky, request.seed);
  if (clouds.length === 0) return { group, onLoaded: Promise.resolve(0) };

  const material = request.enhanced
    ? enhancedCloudMaterial(CLOUD_RAMPS[request.timeOfDay], request.sun ?? { x: 0, y: 1, z: 0 })
    : authenticCloudMaterial();

  const onLoaded = Promise.all(
    CLOUD_MODEL_PATHS.map((path) =>
      loadBakedModel(path)
        .then((body) => {
          const lod = bestDrawableLod(body.lods);
          return lod ? createLodGeometry(lod) : undefined;
        })
        .catch(() => undefined),
    ),
  ).then((geometries) => instanceSlabs(group, clouds, geometries, material));

  return { group, onLoaded };
}
